import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Users as UsersIcon, Search, MapPin, Plus, Edit2, Trash2, Download, X, Save, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { usePincodeAutofill } from '@/hooks/usePincodeAutofill';

interface Address { id: string; address1: string; address2?: string; landmark?: string; city: string; state: string; pincode: string; isDefault?: boolean; label?: string; }
interface Customer { id: string; merchantId: number; name: string; mobile: string; altMobile?: string; email?: string; totalOrders: number; lastOrderAt?: string; serviceType?: 'B2C' | 'B2B' | 'BOTH' | null; addresses: Address[]; }

export function CustomersPage() {
  const { merchantId: urlMid } = useParams<{ merchantId?: string }>();
  const { user: au } = useAuth();
  const isMerchant = au?.kind === 'merchant';
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [serviceType, setServiceType] = useState<''|'B2C'|'B2B'|'BOTH'>('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: merchants = [] } = useQuery({
    queryKey: ['merchants-light'],
    enabled: !isMerchant,
    queryFn: async () => {
      const r = await api.get('/merchants');
      const d: any = r.data;
      return Array.isArray(d) ? d : (d?.items ?? []);
    },
  });

  // merchant users auto-bind to their own merchantId
  const [merchantId, setMerchantId] = useState<number | ''>(isMerchant ? (au as any)?.merchantId ?? '' : (urlMid ? Number(urlMid) : ''));

  const params = useMemo(() => ({ page, pageSize, search: search || undefined, serviceType: serviceType || undefined }), [page, pageSize, search, serviceType]);
  const effectiveMid = isMerchant ? (au as any)?.merchantId : merchantId;
  const { data, isLoading } = useQuery({
    queryKey: ['customers', effectiveMid, params],
    enabled: !!effectiveMid,
    queryFn: async () => (await api.get(`/merchants/${effectiveMid}/customers`, { params })).data as { total: number; items: Customer[] },
  });

  const [showModal, setShowModal] = useState<null | { mode: 'create' } | { mode: 'edit'; cust: Customer }>(null);
  const [showAddrModal, setShowAddrModal] = useState<null | { customerId: string; addr?: Address }>(null);

  const importB2B = useMutation({
    mutationFn: async () => (await api.post('/admin/customers/import-legacy-b2b')).data,
    onSuccess: (d: any) => {
      toast.success(`B2B import done: ${d.distinctCustomers ?? 0} customers across ${d.mappedMerchants ?? 0} merchants (skipped: ${d.skippedNoMerchant ?? 0} no-merchant, ${d.skippedBadMobile ?? 0} bad mobile)`);
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'B2B import failed'),
  });
  const importB2C = useMutation({
    mutationFn: async () => (await api.post('/admin/customers/import-legacy-b2c')).data,
    onSuccess: (d: any) => {
      toast.success(`B2C import done: ${d.distinctCustomers ?? 0} customers across ${d.mappedMerchants ?? 0} merchants (skipped: ${d.skippedNoMerchant ?? 0} no-merchant, ${d.skippedBadMobile ?? 0} bad mobile)`);
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'B2C import failed'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/customers/${id}`)).data,
    onSuccess: () => { toast.success('Customer deleted'); qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const removeAddr = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/addresses/${id}`)).data,
    onSuccess: () => { toast.success('Address removed'); qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const doExport = async () => {
    if (!effectiveMid) return toast.error('Select a merchant first');
    const res = await api.get(`/merchants/${effectiveMid}/customers/export`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a'); a.href = url; a.download = `customers-${effectiveMid}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><UsersIcon size={22} /> Customer Master</h1>
          <p className="text-sm text-slate-500 mt-0.5">Per-merchant address book — autofills on Create Order from mobile</p>
        </div>
        <div className="flex gap-2">
          {!isMerchant && (
            <>
              <button onClick={() => { if (confirm('Import 38,213 legacy B2B customers across all merchants? Safe to re-run (upserts).')) importB2B.mutate(); }}
                disabled={importB2B.isPending}
                className="text-xs px-3 py-1.5 border border-purple-300 text-purple-700 bg-purple-50 rounded hover:bg-purple-100 disabled:opacity-40 flex items-center gap-1">
                {importB2B.isPending ? 'Importing B2B…' : 'Import Legacy B2B'}
              </button>
              <button onClick={() => { if (confirm('Import 275,394 legacy B2C customers across all merchants? May take 2–3 minutes. Safe to re-run.')) importB2C.mutate(); }}
                disabled={importB2C.isPending}
                className="text-xs px-3 py-1.5 border border-sky-300 text-sky-700 bg-sky-50 rounded hover:bg-sky-100 disabled:opacity-40 flex items-center gap-1">
                {importB2C.isPending ? 'Importing B2C…' : 'Import Legacy B2C'}
              </button>
            </>
          )}
          <button onClick={doExport} disabled={!effectiveMid}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"><Download size={13} /> Export CSV</button>
          <button onClick={() => setShowModal({ mode: 'create' })} disabled={!effectiveMid}
            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1"><Plus size={13} /> Add Customer</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {!isMerchant && (
        <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
          className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value="">— Select merchant —</option>
          {merchants.map((m: any) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}
        </select>
        )}
        <div className="relative">
          <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name / mobile / email..."
            className="w-full border border-slate-300 rounded pl-7 pr-2 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-1 text-xs">
          {(['', 'B2C', 'B2B', 'BOTH'] as const).map(t => (
            <button key={t || 'all'} type="button" onClick={() => { setServiceType(t); setPage(1); }}
              className={`px-2.5 py-1 rounded border ${serviceType === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
              {t === '' ? 'All' : t}
            </button>
          ))}
        </div>
      </div>

      {!effectiveMid && !isMerchant && (
        <div className="bg-slate-50 border border-slate-200 rounded p-8 text-center text-slate-400 text-sm">
          Select a merchant to view their customer address book
        </div>
      )}

      {effectiveMid && (
        <>
          <div className="bg-white border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Mobile</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-center px-3 py-2">Orders</th>
                  <th className="text-left px-3 py-2">Addresses</th>
                  <th className="text-left px-3 py-2">Last Order</th>
                  <th className="text-right px-3 py-2 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Loading…</td></tr>}
                {!isLoading && (!data?.items.length) && <tr><td colSpan={8} className="text-center py-6 text-slate-400">No customers yet</td></tr>}
                {data?.items.map(c => (
                  <tr key={c.id} className="border-t hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.mobile}{c.altMobile && <div className="text-slate-400">{c.altMobile}</div>}</td>
                    <td className="px-3 py-2 text-xs">
                      {c.serviceType === 'B2B' && <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-medium text-[10px]">B2B</span>}
                      {c.serviceType === 'B2C' && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-medium text-[10px]">B2C</span>}
                      {c.serviceType === 'BOTH' && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium text-[10px]">B2C + B2B</span>}
                      {!c.serviceType && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{c.email ?? '—'}</td>
                    <td className="px-3 py-2 text-center">{c.totalOrders}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[300px]">
                      <div className="space-y-1">
                        {c.addresses.map(a => (
                          <div key={a.id} className="flex items-start gap-1 group">
                            <MapPin size={10} className="mt-0.5 shrink-0 text-slate-400" />
                            <div className="flex-1 min-w-0">
                              {a.label && <span className="text-[10px] bg-slate-100 text-slate-600 px-1 rounded mr-1">{a.label}</span>}
                              {a.isDefault && <Star size={9} className="inline text-amber-500 fill-current mr-1" />}
                              <span>{a.address1}{a.address2 ? ', ' + a.address2 : ''}, {a.city}, {a.state} - <span className="font-mono">{a.pincode}</span></span>
                            </div>
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                              <button onClick={() => setShowAddrModal({ customerId: c.id, addr: a })} className="text-slate-400 hover:text-indigo-600"><Edit2 size={11} /></button>
                              <button onClick={() => { if (confirm('Delete this address?')) removeAddr.mutate(a.id); }} className="text-slate-400 hover:text-rose-600"><Trash2 size={11} /></button>
                            </div>
                          </div>
                        ))}
                        <button onClick={() => setShowAddrModal({ customerId: c.id })} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5">+ Add address</button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setShowModal({ mode: 'edit', cust: c })} className="text-slate-500 hover:text-indigo-600 mr-2"><Edit2 size={13} /></button>
                      <button onClick={() => { if (confirm(`Delete ${c.name}? This removes ${c.addresses.length} address(es).`)) remove.mutate(c.id); }} className="text-slate-500 hover:text-rose-600"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
          </div>
        </>
      )}

      {showModal && effectiveMid && (
        <CustomerModal merchantId={Number(effectiveMid)} mode={showModal.mode} initial={'cust' in showModal ? showModal.cust : undefined}
          onClose={() => setShowModal(null)}
          onSaved={() => { setShowModal(null); qc.invalidateQueries({ queryKey: ['customers'] }); }} />
      )}
      {showAddrModal && (
        <AddressModal customerId={showAddrModal.customerId} initial={showAddrModal.addr}
          onClose={() => setShowAddrModal(null)}
          onSaved={() => { setShowAddrModal(null); qc.invalidateQueries({ queryKey: ['customers'] }); }} />
      )}
    </div>
  );
}

/* ─── Customer Modal ─────────────────────────────────────────────── */
function CustomerModal({ merchantId, mode, initial, onClose, onSaved }:
  { merchantId: number; mode: 'create' | 'edit'; initial?: Customer; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [mobile, setMobile] = useState(initial?.mobile ?? '');
  const [altMobile, setAlt] = useState(initial?.altMobile ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  // For create, include a first address optionally
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  usePincodeAutofill(pincode, (d) => { setCity(d.city); setState(d.state); });

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        return (await api.post(`/merchants/${merchantId}/customers`, {
          name, mobile, altMobile: altMobile || undefined, email: email || undefined,
          ...(addr1 ? { address1: addr1, address2: addr2 || undefined, city, state, pincode, isDefault: true, label: 'Default' } : {}),
        })).data;
      }
      return (await api.patch(`/customers/${initial!.id}`, { name, altMobile: altMobile || '', email: email || '' })).data;
    },
    onSuccess: () => { toast.success(mode === 'create' ? 'Customer added' : 'Customer updated'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{mode === 'create' ? 'Add Customer' : 'Edit Customer'}</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Name *"><input value={name} onChange={(e) => setName(e.target.value)} className="input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile *"><input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))}
              className="input font-mono" disabled={mode === 'edit'} placeholder="10-digit" /></Field>
            <Field label="Alt Mobile"><input value={altMobile} onChange={(e) => setAlt(e.target.value.replace(/\D/g,'').slice(0,10))} className="input font-mono" /></Field>
          </div>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" /></Field>
          {mode === 'create' && (
            <>
              <div className="border-t pt-3 text-xs text-slate-500">Optional default address</div>
              <Field label="Address Line 1"><input value={addr1} onChange={(e) => setAddr1(e.target.value)} className="input" /></Field>
              <Field label="Address Line 2"><input value={addr2} onChange={(e) => setAddr2(e.target.value)} className="input" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Pincode"><input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g,'').slice(0,6))} className="input font-mono" /></Field>
                <Field label="City"><input value={city} onChange={(e) => setCity(e.target.value)} className="input" /></Field>
                <Field label="State"><input value={state} onChange={(e) => setState(e.target.value)} className="input" /></Field>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!name || mobile.length !== 10 || save.isPending}
            className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
            <Save size={12} /> {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Address Modal ──────────────────────────────────────────────── */
function AddressModal({ customerId, initial, onClose, onSaved }:
  { customerId: string; initial?: Address; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [a1, setA1] = useState(initial?.address1 ?? '');
  const [a2, setA2] = useState(initial?.address2 ?? '');
  const [lm, setLm] = useState(initial?.landmark ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [pincode, setPincode] = useState(initial?.pincode ?? '');
  const [isDefault, setDef] = useState(!!initial?.isDefault);
  usePincodeAutofill(pincode, (d) => { if (!initial) { setCity(d.city); setState(d.state); } });

  const save = useMutation({
    mutationFn: async () => {
      const body = { label: label || undefined, address1: a1, address2: a2 || undefined, landmark: lm || undefined, city, state, pincode, isDefault };
      if (initial) return (await api.patch(`/addresses/${initial.id}`, body)).data;
      return (await api.post(`/customers/${customerId}/addresses`, body)).data;
    },
    onSuccess: () => { toast.success(initial ? 'Address updated' : 'Address added'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{initial ? 'Edit Address' : 'Add Address'}</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} className="input" placeholder="Home / Office" /></Field>
          <Field label="Address Line 1 *"><input value={a1} onChange={(e) => setA1(e.target.value)} className="input" /></Field>
          <Field label="Address Line 2"><input value={a2} onChange={(e) => setA2(e.target.value)} className="input" /></Field>
          <Field label="Landmark"><input value={lm} onChange={(e) => setLm(e.target.value)} className="input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Pincode *"><input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g,'').slice(0,6))} className="input font-mono" /></Field>
            <Field label="City *"><input value={city} onChange={(e) => setCity(e.target.value)} className="input" /></Field>
            <Field label="State *"><input value={state} onChange={(e) => setState(e.target.value)} className="input" /></Field>
          </div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={isDefault} onChange={(e) => setDef(e.target.checked)} /> Set as default address</label>
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!a1 || !city || !state || pincode.length !== 6 || save.isPending}
            className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
            <Save size={12} /> {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-[11px] text-slate-500 mb-1">{label}</div>{children}</div>;
}
